// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { ActionIcon, Box, Group, Loader, Pagination, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import type { ChargeItem } from '@medplum/fhirtypes';
import { STRIPE_PAYMENT_INTENT_IDENTIFIER_SYSTEM } from '@polarissleep/shared';
import { IconPencil, IconSearch } from '@tabler/icons-react';
import { type JSX, useMemo, useState } from 'react';
import { formatMoney, getInvoiceNumber } from '../../../utils/chargeitems';

const PAGE_SIZE = 10;

/** Maps ChargeItem.status to a short human label shown in the Payment Status column. */
function formatPaymentStatus(status: ChargeItem['status']): string {
  switch (status) {
    case 'billable':
    case 'billed':
      return 'Billed';
    case 'planned':
      return 'Planned';
    case 'aborted':
      return 'Aborted';
    case 'entered-in-error':
      return 'Error';
    case 'not-billable':
      return 'Not billable';
    default:
      return status ?? '—';
  }
}

function hasStripePaymentIntent(chargeItem: ChargeItem): boolean {
  return !!chargeItem.identifier?.some((i) => i.system === STRIPE_PAYMENT_INTENT_IDENTIFIER_SYSTEM);
}

function paymentStatusLabel(chargeItem: ChargeItem): string {
  if (chargeItem.status === 'aborted' && hasStripePaymentIntent(chargeItem)) {
    return 'Refunded';
  }
  return formatPaymentStatus(chargeItem.status);
}

function getPurpose(chargeItem: ChargeItem): string {
  return chargeItem.code?.text ?? chargeItem.code?.coding?.[0]?.display ?? chargeItem.code?.coding?.[0]?.code ?? '—';
}

export interface HistoryOfChargesTableProps {
  chargeItems: ChargeItem[];
  loading: boolean;
  canEdit?: boolean;
  onEditChargeItem?: (chargeItem: ChargeItem) => void;
}

export function HistoryOfChargesTable({
  chargeItems,
  loading,
  canEdit = false,
  onEditChargeItem,
}: HistoryOfChargesTableProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return chargeItems;
    }
    return chargeItems.filter((item) => {
      const haystack = [getInvoiceNumber(item), getPurpose(item), paymentStatusLabel(item)].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [chargeItems, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paginated = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  if (loading) {
    return (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        <TextInput
          placeholder="Search charges…"
          value={query}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={14} />}
          size="sm"
          w={260}
          aria-label="Search charges"
        />
      </Group>

      <Box style={{ overflowX: 'auto' }}>
        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Invoice Number</Table.Th>
              <Table.Th>Purpose</Table.Th>
              <Table.Th>Insurance Contribution</Table.Th>
              <Table.Th>Est. Patient Contribution</Table.Th>
              <Table.Th>Actual Patient Contribution</Table.Th>
              <Table.Th>Balance</Table.Th>
              <Table.Th>Due</Table.Th>
              <Table.Th>Payment Status</Table.Th>
              {canEdit && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginated.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={canEdit ? 9 : 8}>
                  <Text ta="center" c="dimmed" py="md">
                    No charges on file.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              paginated.map((item) => (
                <Table.Tr key={item.id}>
                  <Table.Td>{getInvoiceNumber(item)}</Table.Td>
                  <Table.Td>{getPurpose(item)}</Table.Td>
                  <Table.Td c="dimmed">—</Table.Td>
                  <Table.Td c="dimmed">—</Table.Td>
                  <Table.Td c="dimmed">—</Table.Td>
                  <Table.Td>{formatMoney(item.priceOverride?.value, item.priceOverride?.currency)}</Table.Td>
                  <Table.Td c="dimmed">—</Table.Td>
                  <Table.Td>{paymentStatusLabel(item)}</Table.Td>
                  {canEdit && (
                    <Table.Td>
                      <Tooltip label="Edit charge item">
                        <ActionIcon
                          variant="subtle"
                          aria-label={`Edit charge ${getInvoiceNumber(item)}`}
                          onClick={() => onEditChargeItem?.(item)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {filtered.length > PAGE_SIZE && (
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            {`${filtered.length} of ${chargeItems.length}`}
          </Text>
          <Pagination value={pageSafe} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}
    </Stack>
  );
}
